/**
 * The printable HIRE list (2026-09-27) — the hires Table's `Print all` /
 * `Download list (PDF)`: `./listPdf.js`'s A4-landscape list document (the one
 * the order pages print, extracted rather than copied) over HIRE columns, one
 * row per hire LINE, every row of the current tab + search, paging ignored and
 * said so on the page.
 *
 * ── The widths ─────────────────────────────────────────────────────────
 * Printable width 770pt. `width: '*'` columns (Item, Supplier) are flexible
 * — pdfmake auto-divides whatever is left over the FIXED columns' points and
 * the per-column padding, so only the fixed columns' widths need choosing by
 * hand. Status is WORDS, never a colour (a printed page is often greyscale).
 * Money is the payload figure through `moneyOrAbsent` — absent is a dash, never
 * £0.00 — exactly as the Table shows it.
 *
 * 🔀 REORDERED 2026-09-28 to follow the Table's new column order (owner: see
 * `hireListExport.js`'s own note) and given a `po` column — the row has
 * carried `h.po_number` since 2026-09-28 (`c13a99da`) but this document did
 * not print it before.
 */
import { buildListDoc, dateText, td, text } from './listPdf';
import { moneyOrAbsent } from './moneyOrAbsent';
import { getOrderNumber } from './orderTitle';
import { HIRE_STATUS_BADGES } from '../constants/colors';

const statusText = (h) => text((HIRE_STATUS_BADGES[h.hire_status] || {}).label || h.hire_status);

export const HIRE_LIST_COLUMNS = Object.freeze([
  { key: 'po', label: 'PO', width: 46, align: 'left', cell: (h) => td(text(h.po_number)) },
  { key: 'order', label: 'Order', width: 50, align: 'left', cell: (h) => td(text(getOrderNumber(h))) },
  { key: 'requested_by', label: 'Requested By', width: 80, align: 'left', cell: (h) => td(text(h.requested_by)) },
  { key: 'supplier', label: 'Supplier', width: '*', align: 'left', cell: (h) => td(text(h.supplier_name)) },
  { key: 'item', label: 'Item', width: '*', align: 'left', cell: (h) => td(text(h.description)) },
  { key: 'hire_from', label: 'Hire From', width: 52, align: 'left', cell: (h) => td(dateText(h.hire_from)) },
  { key: 'hire_to', label: 'Hire To', width: 52, align: 'left', cell: (h) => td(dateText(h.hire_to)) },
  { key: 'days', label: 'Days', width: 30, align: 'center', cell: (h) => td(text(h.hire_days), 'center') },
  { key: 'expected', label: 'Expected', width: 60, align: 'right', cell: (h) => td(moneyOrAbsent(h.expected_cost), 'right') },
  { key: 'cost', label: 'Cost to Date', width: 64, align: 'right', cell: (h) => td(moneyOrAbsent(h.current_cost), 'right', { bold: true }) },
  { key: 'status', label: 'Status', width: 64, align: 'left', cell: (h) => td(statusText(h)) },
]);

const HIRE_NOUN = Object.freeze({ one: 'hire', many: 'hires' });

/** The whole A4-landscape hire list document. */
export function buildHireListDoc({ hires = [], headerTitle = 'Hires', ...rest } = {}) {
  return buildListDoc({
    ...rest, rows: hires, columns: HIRE_LIST_COLUMNS, noun: HIRE_NOUN, headerTitle, subject: 'Hire List',
  });
}
